import { CheckCircle2 } from 'lucide-react';

const USE_CASES = [
  {
    id: 'service_companies',
    title: 'Service Companies',
    description: 'Plumbers, electricians, HVAC—emergencies don\'t wait for business hours. Neither should your response.',
    bullets: [
      'Capture emergency service requests',
      'Route urgent calls to on-call technicians',
      'Schedule non-urgent appointments',
      'Build customer loyalty with responsiveness',
    ],
    image: '/after-hours/service-hvac-widget.webp',
  },
  {
    id: 'small_businesses',
    title: 'Small Businesses',
    description: 'Can\'t afford overnight staff but don\'t want to miss opportunities? After Hours captures every lead while you sleep.',
    bullets: [
      'Capture leads 24/7 without hiring night staff',
      'Professional image even outside business hours',
      'Wake up to detailed call summaries',
      'Never miss an opportunity again',
    ],
    image: '/after-hours/smallbiz-bakery-widget.webp',
  },
  {
    id: 'property_facilities',
    title: 'Property & Facilities',
    description: 'Property managers, hotels, and building operators need to handle tenant and guest emergencies around the clock.',
    bullets: [
      'Triage urgent vs routine maintenance requests',
      'Route emergencies to on-call staff',
      'Capture detailed issue descriptions',
      'Keep tenants and guests informed',
    ],
    image: '/after-hours/property-hotel-widget.webp',
  },
  {
    id: 'multi_timezone',
    title: 'Global Teams',
    description: 'Serving customers across time zones? Your Genie handles calls when your team is offline.',
    bullets: [
      'Global coverage without global staffing',
      'Country-specific phone numbers',
      'Seamless handoffs between regions',
      'Consistent experience worldwide',
    ],
    image: '/after-hours/global-tech-widget.webp',
  },
];

type UseCase = {
  id: string;
  title: string;
  description: string;
  bullets: string[];
  image: string;
};

const UseCaseCard = ({ useCase }: { useCase: UseCase }) => {
  return (
    <div className="group bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl">
      {/* Image */}
      <div className="relative h-48 sm:h-56 overflow-hidden">
        <img
          src={useCase.image}
          alt={useCase.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
      </div>

      {/* Content */}
      <div className="p-6 sm:p-8">
        {/* Title */}
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
          {useCase.title}
        </h3>

        {/* Description */}
        <p className="text-gray-600 text-base mb-5">
          {useCase.description}
        </p>

        {/* Bullets */}
        <div className="space-y-2.5">
          {useCase.bullets.map((bullet, index) => (
            <div key={index} className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700 text-sm sm:text-base">{bullet}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const AfterHoursOpportunitiesShowcase = () => {
  return (
    <section className="w-full bg-gray-50 py-16 md:py-24 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
            Stop Losing Business After Hours
          </h2>
          <p className="text-lg md:text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto">
            Every missed call is a missed opportunity. See how businesses like yours are capturing revenue around the clock.
          </p>
        </div>

        {/* 2x2 Grid of Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {USE_CASES.map((useCase: UseCase) => (
            <UseCaseCard key={useCase.id} useCase={useCase} />
          ))}
        </div>
      </div>
    </section>
  );
};
